import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { AxiosError } from 'axios';
import { ErrorData } from '../../../../types';
import useSnackBar from '../../../../hooks/useSnackBar';
import {
  setFirmExternalVendorId,
  removeFirmExternalVendorId,
} from '../../../../api/firmsApi/firmsApi';
import ConfirmationPopup from '../../../../components/ConfirmationPopup';
import FindNetSuiteVendorPopup from '../../../../components/FindNetSuiteVendorPopup';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  Box,
  Tooltip,
  Button,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TablePaginationLocalContainer from '../../../../components/Table/TablePaginationLocalContainer';
import { FIRM_ROLES } from '../../../CasesView/types';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import { accountingVendorType } from '../../../VendorsView/helpers';
import { ROWS_PER_PAGE_OPTIONS, NETSUITE_LABEL } from '../../../../constants/constants';
import TruncatedText from '../../../../components/TruncatedText';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme) => ({
  accordion: {
    backgroundColor: 'transparent',
    marginTop: theme.spacing(1),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Inter',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Inter',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    fontFamily: 'Inter',
  },
  tableContainer: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Inter',
    // minHeight: isLongData ? '300px' : 'inherit',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      padding: '16px 12px 16px 8px',
      minWidth: '140px',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordWrap: 'break-word',
      wordBreak: 'break-word',
    },
  },
  labelText: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
    color: '#FFFFFFB3',
  },
  columnName: {
    minWidth: 320,
    maxWidth: 320,
  },
  columnAmount: {
    minWidth: 210,
    maxWidth: 210,
  },

  columnAction: {
    minWidth: 100,
    maxWidth: 100,
  },
  columnActionWide: {
    minWidth: 180,
    maxWidth: 180,
  },
}));

interface AttorneyFeeItem {
  id: number;
  name: string;
  fee: number;
  role: string;
  disposition: string;
  paymentMethod: string;
  accounting?: {
    isSynced: boolean;
    externalVendorId: string;
    link: string | null;
    lastSyncedAt: string | null;
    status: string | null;
  };
}

type AttorneysFees = AttorneyFeeItem[];

interface AttorneyFeesTableProps {
  handleMenuOptionClick: (option: string, index: number) => void;
  fields: AttorneysFees;
  attorneyFeesTotal: number;
  onFieldsUpdate?: (updatedField: AttorneyFeeItem) => void;
}

const DraftAttorneyFeeTable = ({
  fields,
  attorneyFeesTotal,
  onFieldsUpdate,
}: AttorneyFeesTableProps) => {
  const isLongData = Array.isArray(fields) && fields.length > 5;
  const styles = useStyles({ isLongData });
  const { setAlert } = useSnackBar();
  const theme = useTheme();
  const hasAccounting = fields.some(
    (field) => field.accounting !== undefined && field.accounting !== null,
  );

  // Per-firm sync state: map of firmId -> isSynced
  const [syncedMap, setSyncedMap] = useState<Record<number, boolean>>(() => {
    const map: Record<number, boolean> = {};
    fields.forEach((f) => {
      map[f.id] = f.accounting?.isSynced ?? false;
    });
    return map;
  });

  // Per-firm link state: map of firmId -> link
  const [linkMap, setLinkMap] = useState<Record<number, string | null>>(() => {
    const map: Record<number, string | null> = {};
    fields.forEach((f) => {
      map[f.id] = f.accounting?.link ?? null;
    });
    return map;
  });

  const [activeFirmId, setActiveFirmId] = useState<number | null>(null);
  const [activeFirmName, setActiveFirmName] = useState('');
  const [syncAction, setSyncAction] = useState<'sync' | 'relink'>('sync');
  const [isNetSuitePopupOpen, setIsNetSuitePopupOpen] = useState(false);
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

  const vendorType = accountingVendorType;

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const paginatedFields = Array.isArray(fields)
    ? fields.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  const openNetSuitePopup = (firm: AttorneyFeeItem, action: 'sync' | 'relink') => {
    setActiveFirmId(firm.id);
    setActiveFirmName(firm.name);
    setSyncAction(action);
    setIsNetSuitePopupOpen(true);
  };

  // Sync/relink directly from the review popup, no confirmation step
  const handleNetSuiteVendorSelect = async (internalId: string) => {
    if (!activeFirmId || !internalId) return;
    try {
      const response = await setFirmExternalVendorId(activeFirmId, internalId);
      setAlert(() => ({
        message:
          response.data.message ||
          `Firm ${syncAction === 'sync' ? 'synced' : 'relinked'} successfully.`,
        type: 'success',
      }));
      const newIsSynced = response.data.isSynced ?? false;
      setSyncedMap((prev) => ({ ...prev, [activeFirmId]: newIsSynced }));
      setLinkMap((prev) => ({ ...prev, [activeFirmId]: response.data.link ?? null }));

      // Find the updated field and notify parent
      const updatedField = fields.find((f) => f.id === activeFirmId);
      if (updatedField && onFieldsUpdate) {
        onFieldsUpdate({
          ...updatedField,
          accounting: {
            isSynced: newIsSynced,
            externalVendorId: response.data.externalVendorId ?? '',
            link: response.data.link ?? null,
            lastSyncedAt: response.data.lastSyncedAt ?? null,
            status: response.data.status ?? null,
          },
        });
      }

      setIsNetSuitePopupOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data?.message || `Failed to ${syncAction} firm.`,
        type: 'error',
      }));
    }
  };

  const handleConfirmUnlink = async () => {
    if (!activeFirmId) return;
    try {
      const response = await removeFirmExternalVendorId(activeFirmId);
      setAlert(() => ({
        message: response.data.message || 'Firm unlinked successfully.',
        type: 'success',
      }));
      setSyncedMap((prev) => ({ ...prev, [activeFirmId]: false }));
      setLinkMap((prev) => ({ ...prev, [activeFirmId]: null }));
      // Find the updated field and notify parent
      const updatedField = fields.find((f) => f.id === activeFirmId);
      if (updatedField && onFieldsUpdate) {
        onFieldsUpdate({
          ...updatedField,
          accounting: {
            isSynced: false,
            externalVendorId: updatedField.accounting?.externalVendorId ?? '',
            link: null,
            lastSyncedAt: null,
            status: null,
          },
        });
      }

      setIsUnlinkConfirmOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data?.message || 'Failed to unlink firm.',
        type: 'error',
      }));
    }
  };

  return (
    <>
      <Accordion className={styles.classes.accordion} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          className={styles.classes.accordionSummary}
        >
          {/* <Typography>Attorney Fees</Typography> */}
          <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
            <Typography>Attorney Fees</Typography>
            <Typography className={styles.classes.labelText}>
              {numberToUSDCurrency(attorneyFeesTotal)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails className={styles.classes.accordionDetails}>
          {/* {attorneyFeesFields?.length > 0 ? ( */}
          <TableContainer
            className={styles.classes.tableContainer}
            sx={{ width: '99%' }}
            component={Paper}
            elevation={0}
          >
            <Table sx={{ minWidth: '430px', borderCollapse: 'collapse' }}>
              <TableHead className={styles.classes.tableHeaderCell}>
                <TableRow>
                  <TableCell key={'name'} className={styles.classes.columnName}>
                    {'Name'}
                  </TableCell>
                  <TableCell key={'amount'} className={styles.classes.columnAmount}>
                    {'Amount'}
                  </TableCell>
                  {hasAccounting && (
                    <>
                      <TableCell
                        key={'confirm'}
                        className={styles.classes.columnAction}
                      ></TableCell>
                      <TableCell key={'action'} className={styles.classes.columnActionWide}>
                        {'Action'}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody className={clsx(styles.classes.tableBodyCell)}>
                {paginatedFields.length > 0 ? (
                  paginatedFields.map((field: AttorneyFeeItem) => {
                    const isSynced = syncedMap[field.id] ?? false;
                    const firmLink = linkMap[field.id] ?? null;
                    return (
                      <TableRow key={field.id}>
                        <TableCell className={styles.classes.columnName}>
                          <>
                            <Box display="flex" alignItems="center" gap="8px" overflow="hidden">
                              <TruncatedText text={field.name || '-'} maxLength={30} />
                              {firmLink != null && (
                                <Tooltip
                                  title={`View Firm in ${NETSUITE_LABEL}`}
                                  componentsProps={{
                                    tooltip: {
                                      sx: (theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        color: theme.palette.primary.light,
                                        borderRadius: '5px',
                                        boxShadow: '3',
                                        fontFamily: 'Inter',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        padding: '5px',
                                        maxWidth: '140px',
                                      }),
                                    },
                                  }}
                                >
                                  <LaunchIcon
                                    sx={{
                                      cursor: 'pointer',
                                      color: 'common.white',
                                      fontSize: '20px',
                                      flexShrink: 0,
                                    }}
                                    onClick={() => window.open(firmLink || '', '_blank')}
                                  />
                                </Tooltip>
                              )}
                              {field.accounting && (
                                <Box
                                  sx={{
                                    backgroundColor: isSynced ? 'success.main' : 'warning.main',
                                    color: 'white',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                  }}
                                >
                                  {isSynced ? 'Synced' : 'Unsynced'}
                                </Box>
                              )}
                            </Box>
                            {field.role && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ display: 'block', fontSize: '12px' }}
                              >
                                {FIRM_ROLES[field.role as keyof typeof FIRM_ROLES]}
                              </Typography>
                            )}
                          </>
                        </TableCell>
                        <TableCell className={styles.classes.columnAmount}>
                          {numberToUSDCurrency(field.fee) || '-'}
                        </TableCell>
                        {field.accounting && (
                          <>
                            <TableCell className={styles.classes.columnAction}></TableCell>
                            <TableCell className={styles.classes.columnActionWide}>
                              <Box display="flex" gap="8px" flexWrap="nowrap" alignItems="center">
                                {field.accounting && !isSynced && (
                                  <Button
                                    variant="contained"
                                    startIcon={<SearchIcon sx={{ fontSize: '18px' }} />}
                                    onClick={() => openNetSuitePopup(field, 'sync')}
                                    sx={{
                                      backgroundColor: theme.palette.info.main,
                                      color: 'white',
                                      textTransform: 'none',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      padding: '6px 10px',
                                      borderRadius: '12px',
                                      width: '182px',
                                      whiteSpace: 'nowrap',
                                      '&:hover': { backgroundColor: theme.palette.info.dark },
                                    }}
                                  >
                                    Match firm
                                  </Button>
                                )}
                                {field.accounting && isSynced && (
                                  <>
                                    <Button
                                      variant="contained"
                                      startIcon={<LinkIcon />}
                                      onClick={() => openNetSuitePopup(field, 'relink')}
                                      sx={{
                                        backgroundColor: theme.palette.info.main,
                                        color: 'white',
                                        textTransform: 'none',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        padding: '6px 10px',
                                        borderRadius: '12px',
                                        '&:hover': { backgroundColor: theme.palette.info.dark },
                                      }}
                                    >
                                      Relink
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      startIcon={<LinkOffIcon />}
                                      onClick={() => {
                                        setActiveFirmId(field.id);
                                        setActiveFirmName(field.name);
                                        setIsUnlinkConfirmOpen(true);
                                      }}
                                      sx={{
                                        borderColor: theme.palette.error.main,
                                        color: theme.palette.error.main,
                                        textTransform: 'none',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        padding: '6px 10px',
                                        borderRadius: '12px',
                                        '&:hover': {
                                          borderColor: theme.palette.error.dark,
                                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                        },
                                      }}
                                    >
                                      Unlink
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        sx={{
                          color: 'text.disabled',
                          fontSize: '14px',
                          textAlign: 'center',
                          padding: '20px',
                        }}
                      >
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePaginationLocalContainer
            count={Array.isArray(fields) ? fields.length : 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value as string, ROWS_PER_PAGE_OPTIONS[0]));
              setPage(0);
            }}
            totalPages={Array.isArray(fields) ? Math.ceil(fields.length / rowsPerPage) : 0}
          />
        </AccordionDetails>
      </Accordion>

      <FindNetSuiteVendorPopup
        open={isNetSuitePopupOpen}
        lienName={activeFirmName}
        searchTerm={activeFirmName}
        entityType="Firm"
        entityLabel="Firm"
        entityId={activeFirmId ?? undefined}
        onClose={() => setIsNetSuitePopupOpen(false)}
        onVendorSelectFromNetsuite={handleNetSuiteVendorSelect}
        syncAction={syncAction}
      />

      <ConfirmationPopup
        open={isUnlinkConfirmOpen}
        title="Confirm Unlink"
        message={`Are you sure you want to unlink this Firm ${activeFirmName} from ${vendorType}? This action will remove the connection between V.Law and ${vendorType} for this firm.`}
        confirmText="Unlink"
        onConfirm={handleConfirmUnlink}
        onCancel={() => setIsUnlinkConfirmOpen(false)}
      />
    </>
  );
};

export default DraftAttorneyFeeTable;
